import api from '@/services/api';
import type { CampaignTransferRule, UpdateTransferRulePayload } from '../types/transferRule.types';
import type { TransferRuleFormValues } from '../schemas/transferRule.schema';

const smrPath = '/smr';

/** GET the campaign's transfer rules (existing row, or 4a's typed defaults). Envelope-unwrapped. */
export const fetchCampaignTransferRules = async (campaignId: string): Promise<CampaignTransferRule> => {
    const response = await api.get(`${smrPath}/campaigns/${campaignId}/transfer-rules`);
    return (response.data.data ?? response.data) as CampaignTransferRule;
};

/** Wire → form: null policy becomes the Select's '' sentinel; directions drop any wire-only keys. */
export const toTransferRuleFormValues = (data: CampaignTransferRule): TransferRuleFormValues => ({
    transfer_in_allowed: data.transfer_in_allowed,
    transfer_out_allowed: data.transfer_out_allowed,
    transfer_in_policy: data.transfer_in_policy ?? '',
    transfer_out_policy: data.transfer_out_policy ?? '',
    forbidden_directions: (data.forbidden_directions ?? []).map((d) => ({
        from_scope: d.from_scope,
        to_scope: d.to_scope,
    })),
});

/**
 * Boundary shaping: '' policy → null; and every forbid-list entry is rebuilt as a fresh
 * {from_scope,to_scope} literal so no UI-only key (notably useFieldArray's synthetic `id`)
 * can leak past the FormRequest's array:from_scope,to_scope allowlist.
 */
export const toTransferRulePayload = (values: TransferRuleFormValues): UpdateTransferRulePayload => ({
    transfer_in_allowed: values.transfer_in_allowed,
    transfer_out_allowed: values.transfer_out_allowed,
    transfer_in_policy: values.transfer_in_policy === '' ? null : values.transfer_in_policy,
    transfer_out_policy: values.transfer_out_policy === '' ? null : values.transfer_out_policy,
    forbidden_directions: values.forbidden_directions.map((d) => ({
        from_scope: d.from_scope,
        to_scope: d.to_scope,
    })),
});

/** PUT the whole policy. Returns the persisted (4a-normalised) row from the {message,data} envelope. */
export const updateCampaignTransferRules = async (
    campaignId: string,
    payload: UpdateTransferRulePayload,
): Promise<CampaignTransferRule> => {
    const response = await api.put(`${smrPath}/campaigns/${campaignId}/transfer-rules`, payload);
    return (response.data.data ?? response.data) as CampaignTransferRule;
};
